<template>
	<div class="chat-widget-root">
		<!-- Botão flutuante (FAB) -->
		<v-btn
			v-if="!isOpen"
			class="chat-fab"
			data-tour="assistant"
			color="primary"
			icon
			size="large"
			elevation="6"
			:aria-label="t('chat.openLabel')"
			@click="open"
		>
			<v-icon size="28">mdi-robot-happy-outline</v-icon>
		</v-btn>

		<!-- Painel do chat -->
		<v-card
			v-if="isOpen"
			ref="panelEl"
			class="chat-panel"
			:class="{ 'chat-panel--large': panelSize === 'large' }"
			elevation="10"
			rounded="lg"
		>
			<!-- Cabeçalho -->
			<v-card-title
				class="bg-primary text-white d-flex align-center ga-2 py-2 px-3"
			>
				<v-icon size="22">mdi-robot-happy-outline</v-icon>
				<!-- min-width 0 deixa o título truncar: no celular o subtítulo empurrava o X para fora. -->
				<div class="chat-title d-flex flex-column">
					<span class="text-subtitle-2 font-weight-bold">{{
						t("chat.title")
					}}</span>
					<span class="text-caption text-white opacity-80">{{
						t("chat.subtitle")
					}}</span>
				</div>
				<v-spacer />
				<v-btn
					variant="text"
					icon
					size="small"
					color="white"
					:aria-label="t('chat.clearLabel')"
					:disabled="messages.length === 0 || loading"
					@click="clear"
				>
					<v-icon size="20">mdi-broom</v-icon>
					<v-tooltip activator="parent" location="bottom">{{
						t("chat.clearLabel")
					}}</v-tooltip>
				</v-btn>
				<v-btn
					variant="text"
					icon
					size="small"
					color="white"
					:aria-label="isExpanded ? t('chat.collapseLabel') : t('chat.expandLabel')"
					@click="toggleExpanded"
				>
					<v-icon size="20">{{
						isExpanded ? "mdi-fullscreen-exit" : "mdi-fullscreen"
					}}</v-icon>
				</v-btn>
				<v-btn
					variant="text"
					icon
					size="small"
					color="white"
					:aria-label="t('chat.closeLabel')"
					@click="close"
				>
					<v-icon size="20">mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<!-- Corpo: mensagens -->
			<div ref="scrollArea" class="chat-body" @click="onBubbleClick">
				<!-- Estado vazio / boas-vindas -->
				<div v-if="messages.length === 0 && !loading" class="chat-empty">
					<v-icon size="48" color="primary" class="mb-2"
						>mdi-robot-happy-outline</v-icon
					>
					<p class="text-subtitle-1 font-weight-medium mb-1">
						{{ t("chat.emptyTitle") }}
					</p>
					<p class="text-body-2 text-medium-emphasis mb-4">
						{{ t("chat.emptyHint") }}
					</p>
					<div class="d-flex flex-column ga-2 chat-suggestions">
						<v-chip
							v-for="(s, i) in suggestions"
							:key="i"
							variant="outlined"
							color="primary"
							size="small"
							@click="sendSuggestion(s)"
						>
							{{ s }}
						</v-chip>
					</div>
				</div>

				<!-- Mensagens -->
				<div
					v-for="msg in messages"
					:key="msg.id"
					class="chat-msg"
					:class="msg.role === 'USER' ? 'chat-msg--user' : 'chat-msg--bot'"
				>
					<div v-if="msg.role === 'ASSISTANT'" class="chat-bot-content">
						<!-- eslint-disable-next-line vue/no-v-html -- conteúdo sanitizado por allowlist em sanitize() -->
						<div class="chat-bubble chat-markdown" v-html="renderMarkdown(msg.content)"></div>

						<!-- Cards de confirmação de criação -->
						<div
							v-for="action in msg.actions || []"
							v-show="actionState[action.id] !== 'cancelled'"
							:key="action.id"
							class="chat-action-card"
						>
							<div class="chat-action-title">
								<v-icon size="16">mdi-plus-circle-outline</v-icon>
								{{ action.title }}
							</div>
							<div
								v-for="(f, i) in action.display"
								:key="i"
								class="chat-action-row"
							>
								<span class="chat-action-label">{{ f.label }}</span>
								<v-text-field
									v-if="editingAction[action.id] && f.editable && f.key"
									v-model="drafts[action.id][f.key]"
									:label="f.label"
									density="compact"
									variant="outlined"
									hide-details
									class="chat-action-input"
								/>
								<!-- Ícone e cor aparecem de verdade: "mdi-feather" e "#8B4513" crus não dizem nada a quem confirma. -->
								<v-icon v-else-if="isIconField(f)" size="18">{{ f.value }}</v-icon>
								<span
									v-else-if="isColorField(f)"
									class="chat-action-swatch"
									:style="{ backgroundColor: f.value }"
								/>
								<span v-else class="chat-action-value">{{ f.value }}</span>
							</div>

							<div v-if="actionError[action.id]" class="chat-action-error" role="alert">
								{{ actionError[action.id] }}
							</div>

							<div v-if="actionState[action.id] === 'done'" class="chat-action-done">
								<v-icon size="16" color="success">mdi-check-circle</v-icon>
								<span>{{ t("chat.actions.created") }}</span>
								<v-spacer />
								<v-btn
									size="small"
									variant="tonal"
									color="primary"
									:disabled="loading"
									@click="send(t('chat.actions.continueMsg'))"
								>
									{{ t("chat.actions.continue") }}
								</v-btn>
							</div>
							<div
								v-else-if="actionState[action.id] === 'executing'"
								class="chat-action-executing"
							>
								{{ t("chat.actions.checkOnScreen") }}
							</div>
							<div v-else class="chat-action-buttons">
								<v-btn
									size="small"
									variant="text"
									:disabled="actionState[action.id] === 'creating'"
									@click="cancelAction(action)"
								>
									{{ t("chat.actions.cancel") }}
								</v-btn>
								<v-btn
									v-if="action.display.some((f) => f.editable)"
									size="small"
									variant="text"
									:disabled="actionState[action.id] === 'creating'"
									@click="toggleEdit(action)"
								>
									{{ t("chat.actions.edit") }}
								</v-btn>
								<v-btn
									size="small"
									color="primary"
									variant="flat"
									:loading="actionState[action.id] === 'creating'"
									@click="confirmAction(action)"
								>
									{{ t("chat.actions.confirm") }}
								</v-btn>
							</div>
						</div>
						<!-- Botões de resposta: só na última mensagem, que é a pergunta ainda
						     sem resposta. Clicar é o mesmo que digitar o texto do botão.
						     DENTRO da coluna da fala, para ficarem embaixo da caixa — como irmãos
						     dela, na linha flex da mensagem, saíam à direita (visto em HML). -->
						<div
							v-if="msg.id === quickRepliesMessageId"
							class="chat-quick-replies"
							role="group"
							:aria-label="t('chat.quickReplies.label')"
						>
							<v-btn
								v-for="option in msg.quickReplies"
								:key="option"
								size="small"
								variant="outlined"
								color="primary"
								rounded="lg"
								:disabled="loading"
								@click="send(msg.replyMessages?.[option] ?? option)"
							>
								{{ option }}
							</v-btn>
						</div>
					</div>
					<div v-else class="chat-bubble">{{ msg.content }}</div>
				</div>

				<!-- Indicador de digitação -->
				<div v-if="loading" class="chat-msg chat-msg--bot">
					<div class="chat-bubble d-flex align-center ga-2">
						<v-progress-circular indeterminate size="16" width="2" color="primary" />
						<span class="text-caption text-medium-emphasis">{{
							t("chat.thinking")
						}}</span>
					</div>
				</div>
			</div>

			<!-- Rodapé: input -->
			<div class="chat-footer">
				<v-textarea
					ref="inputRef"
					v-model="input"
					:placeholder="t('chat.placeholder')"
					variant="outlined"
					density="compact"
					rows="1"
					max-rows="4"
					auto-grow
					hide-details
					:disabled="loading"
					class="chat-input"
					@keydown.enter.exact.prevent="send"
				>
					<template #append-inner>
						<v-btn
							icon
							variant="text"
							color="primary"
							size="small"
							:disabled="!input.trim() || loading"
							:aria-label="t('chat.send')"
							@click="send"
						>
							<v-icon>mdi-send</v-icon>
						</v-btn>
					</template>
				</v-textarea>
				<p class="chat-disclaimer text-caption text-medium-emphasis">
					{{ t("chat.disclaimer") }}
				</p>
			</div>
		</v-card>

		<!-- Confirmação de limpar conversa -->
		<v-dialog v-model="confirmClearOpen" max-width="380">
			<v-card rounded="lg" elevation="1">
				<v-card-title class="bg-error text-white d-flex align-center ga-2 py-2 px-4">
					<v-icon size="20">mdi-broom</v-icon>
					{{ t("chat.clearLabel") }}
				</v-card-title>
				<v-card-text class="pt-4 text-body-2">
					{{ t("chat.clearConfirm") }}
				</v-card-text>
				<v-card-actions class="px-4 pb-3">
					<v-spacer />
					<v-btn variant="text" @click="confirmClearOpen = false">
						{{ t("chat.actions.cancel") }}
					</v-btn>
					<v-btn color="error" variant="flat" @click="doClear">
						{{ t("chat.actions.confirm") }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useNotification } from "@/composables/useNotification";
import { useAssistant } from "@/composables/useAssistant";
import { useEntityRefresh } from "@/composables/useEntityRefresh";
import {
	getChatHistory,
	sendChatMessage,
	clearChatHistory,
	updateAssistantActionStatus,
	type ChatMessage,
	type PendingAction,
} from "@/services/chatService";
import { executePendingAction } from "@/services/chatActions";
import { renderMarkdown, isInternalHref } from "./chatSanitize";

type ChatMessageUI = ChatMessage & {
	actions?: PendingAction[];
	/**
	 * Pergunta feita pela tela: o que cada botão envia (o rótulo é curto, a
	 * mensagem é a completa). Sem isto, o clique envia o próprio rótulo.
	 */
	replyMessages?: Record<string, string>;
	/** Pergunta feita pela tela: contexto para quem responde digitando. */
	typedAnswerPrefix?: string;
};

/** Sem acento, caixa nem pontuação: "manual." e "Manual" são o mesmo botão. */
const normalizeAnswer = (text: string) =>
	text
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, "")
		.trim();

/**
 * Resposta DIGITADA a uma pergunta feita pela tela. A pergunta não existe no
 * servidor, então mandar só "quero que você faça" deixava o modelo sem saber do
 * que se tratava (revisão do Codex). Digitar o rótulo vale o botão; o resto vai
 * junto do contexto da pergunta.
 */
const answerLocalQuestion = (question: ChatMessageUI, typed: string): string => {
	const label = Object.keys(question.replyMessages ?? {}).find(
		(option) => normalizeAnswer(option) === normalizeAnswer(typed),
	);
	if (label) return question.replyMessages![label];
	return question.typedAnswerPrefix ? `${question.typedAnswerPrefix} ${typed}` : typed;
};
type ActionStatus = "idle" | "creating" | "executing" | "done" | "cancelled";

const { t } = useI18n();
const router = useRouter();
const notification = useNotification();

// O apiService já mostra toast global para 5xx, 429 e 408 (e erro de rede).
// O widget só deve notificar os 4xx restantes (400/401/403/404/409/422).
const widgetShouldToast = (status?: number): boolean =>
	!!status && status < 500 && status !== 429 && status !== 408;

const {
	isOpen,
	seedPrompt,
	localQuestion,
	panelSize,
	close,
	toggleExpanded,
	onNavigate,
} = useAssistant();
const { notifyEntityChanged } = useEntityRefresh();
const isExpanded = computed(() => panelSize.value === "large");
/** Abaixo disso a meia tela (mínimo 560px) cobre quase tudo: breakpoint md do Vuetify. */
const NARROW_SCREEN_MAX = 960;
/** Campo de mensagem: recebe o foco de volta quando a resposta chega. */
const inputRef = ref<{ focus?: () => void; $el?: HTMLElement } | null>(null);
// `.chat-panel` é um v-card: a ref é a instância do componente, e o elemento
// fica em `$el` (em teste, com o v-card como elemento comum, é o próprio nó).
const panelEl = ref<HTMLElement | { $el?: HTMLElement } | null>(null);
const panelRoot = (): HTMLElement | null => {
	const panel = panelEl.value;
	return panel instanceof HTMLElement ? panel : (panel?.$el ?? null);
};

/**
 * O usuário está digitando em OUTRO lugar? Então a resposta não pode puxar o
 * foco (code-reviewer): a meia tela existe para mexer na tela ao lado, e o
 * "Alterar" de um card tem campos próprios dentro do chat.
 */
const isTypingElsewhere = () => {
	const active = document.activeElement as HTMLElement | null;
	if (!active || active === document.body) return false;
	if (inputRef.value?.$el?.contains(active)) return false;
	// Fora do painel: é a página, e ela é do usuário.
	if (!panelRoot()?.contains(active)) return true;
	// Dentro do painel, só campo editável conta (botão clicado não é digitação).
	return active.matches('input, textarea, select, [contenteditable="true"]');
};

/**
 * Devolve o foco ao campo de mensagem (pedido do usuário, 18/09/2026). Ele fica
 * desabilitado enquanto a resposta não chega, e desabilitar tira o foco — sem
 * isto era preciso clicar no campo a cada resposta. No celular não: focar abre o
 * teclado, que cobriria a resposta que acabou de chegar.
 */
const focusInput = async () => {
	if (window.innerWidth < NARROW_SCREEN_MAX) return;
	// Espera o campo ser reabilitado: foco em campo desabilitado não pega.
	await nextTick();
	if (isTypingElsewhere()) return;
	inputRef.value?.focus?.();
};
const confirmClearOpen = ref(false);
const loading = ref(false);
const loadedHistory = ref(false);
const input = ref("");
const conversationId = ref<string | null>(null);
const messages = ref<ChatMessageUI[]>([]);

// A pergunta com botões é sempre a ÚLTIMA mensagem: qualquer mensagem depois
// dela (clique ou texto digitado) já é a resposta, e os botões saem de cena.
const quickRepliesMessageId = computed(() => {
	const last = messages.value[messages.value.length - 1];
	return last?.role === "ASSISTANT" && last.quickReplies?.length
		? last.id
		: null;
});
const actionState = ref<Record<string, ActionStatus>>({});
// Erro fica no próprio card, com o motivo da API: o aviso genérico no canto sumia
// e o usuário achava que o Confirmar não tinha feito nada.
const actionError = ref<Record<string, string>>({});

// Decide pelo campo, não só pelo valor: um nome "mdi-feather" continua texto.
// Os rótulos vêm do backend (FIELD_LABELS do assistente).
// Alterar: rascunho por card dos campos editáveis. Fechar o Alterar aplica o
// rascunho ao card, então o que o usuário vê é sempre o que o Confirmar envia.
const editingAction = ref<Record<string, boolean>>({});
const drafts = ref<Record<string, Record<string, string>>>({});
const editedActions = ref<Record<string, boolean>>({});
/** Campos que o cadastro exige: vazio não vai para a API. */
const REQUIRED_EDIT_KEYS = new Set(["name", "code", "label", "firstName", "lastName"]);

const editableKeys = (action: PendingAction) =>
	action.display.filter((f) => f.editable && f.key).map((f) => f.key as string);

const applyDraft = (action: PendingAction) => {
	const draft = drafts.value[action.id];
	if (!draft) return;
	for (const [key, raw] of Object.entries(draft)) {
		const value = raw.trim();
		action.payload[key] = value;
		const field = action.display.find((f) => f.key === key);
		if (field) field.value = value;
	}
	delete drafts.value[action.id];
	editedActions.value[action.id] = true;
	editingAction.value[action.id] = false;
};

const toggleEdit = (action: PendingAction) => {
	if (editingAction.value[action.id]) {
		applyDraft(action);
		return;
	}
	drafts.value[action.id] = Object.fromEntries(
		editableKeys(action).map((key) => [
			key,
			String(action.payload[key] ?? action.display.find((f) => f.key === key)?.value ?? ""),
		]),
	);
	editingAction.value[action.id] = true;
};

const isIconField = (f: { label: string; value: string }) =>
	f.label === "Ícone" && /^mdi-[a-z0-9-]+$/.test(f.value);
const isColorField = (f: { label: string; value: string }) =>
	f.label === "Cor" && /^#[0-9a-f]{6}$/i.test(f.value);

const errorReason = (err: any): string => {
	const message = err?.response?.data?.message;
	if (Array.isArray(message)) return message.filter(Boolean).join("; ");
	return typeof message === "string" ? message : "";
};
const scrollArea = ref<HTMLElement | null>(null);

const suggestions = computed<string[]>(() => [
	t("chat.suggestions.s1"),
	t("chat.suggestions.s2"),
	t("chat.suggestions.s3"),
]);

const scrollToBottom = async () => {
	await nextTick();
	if (scrollArea.value) {
		scrollArea.value.scrollTop = scrollArea.value.scrollHeight;
	}
};

// Alinha o TOPO da última mensagem ao topo da área visível (para ler do começo).
const scrollNewMsgToTop = async () => {
	await nextTick();
	const c = scrollArea.value;
	if (!c) return;
	const items = c.querySelectorAll<HTMLElement>(".chat-msg");
	const last = items[items.length - 1];
	if (!last) {
		c.scrollTo({ top: c.scrollHeight, behavior: "smooth" });
		return;
	}
	// Rola suavemente (animado) até o topo da última mensagem, sem "pulo".
	const target =
		c.scrollTop +
		last.getBoundingClientRect().top -
		c.getBoundingClientRect().top;
	c.scrollTo({ top: target, behavior: "smooth" });
};

const open = async () => {
	isOpen.value = true;
	if (!loadedHistory.value) {
		await loadHistory();
	}
};

// Uma carga por vez: dois acionamentos antes de o histórico chegar abriam duas
// consultas, e a que chegava por último apagava a conversa — inclusive uma
// resposta já recebida (revisão do Codex).
let historyLoad: Promise<void> | null = null;
const loadHistory = () => (historyLoad ??= fetchHistory());

const fetchHistory = async () => {
	loading.value = true;
	try {
		const data = await getChatHistory();
		conversationId.value = data.conversationId;
		messages.value = data.messages;
		// Card salvo volta no estado em que ficou: confirmado não oferece Confirmar de novo.
		for (const msg of data.messages) {
			for (const action of msg.actions ?? []) {
				actionState.value[action.id] = stateFromStatus(action.status);
			}
		}
		loadedHistory.value = true;
		await scrollToBottom();
	} catch {
		// silencioso: histórico ausente não é erro crítico
		loadedHistory.value = true;
	} finally {
		loading.value = false;
	}
};

const send = async (override?: string) => {
	// Quando chamado por evento (@click/@keydown), o 1º arg é o Event → ignora.
	const typed = typeof override !== "string";
	const raw = (typed ? input.value : override).trim();
	if (!raw || loading.value) return;
	const pending = messages.value[messages.value.length - 1];
	const text =
		typed && pending?.replyMessages ? answerLocalQuestion(pending, raw) : raw;

	if (typeof override !== "string") input.value = "";
	messages.value.push({
		id: `local-${Date.now()}`,
		role: "USER",
		content: text,
		createdAt: new Date().toISOString(),
	});
	await scrollNewMsgToTop();

	loading.value = true;
	try {
		const res = await sendChatMessage(text, conversationId.value);
		conversationId.value = res.conversationId;
		const replyUi: ChatMessageUI = { ...res.reply };
		if (res.quickReplies?.length) replyUi.quickReplies = res.quickReplies;
		if (res.pendingActions?.length) {
			replyUi.actions = res.pendingActions;
			res.pendingActions.forEach((a) => {
				actionState.value[a.id] = "idle";
			});
		}
		messages.value.push(replyUi);
	} catch (err: any) {
		// 5xx/rede/429/408 já têm toast global do apiService; só tratamos os 4xx restantes.
		if (widgetShouldToast(err?.response?.status)) {
			notification.error(t("chat.errorGeneric"));
		}
	} finally {
		loading.value = false;
	}
	// Rola só após o "digitando" sumir, para alinhar o TOPO da resposta (não o indicador).
	await scrollNewMsgToTop();
	await focusInput();
};

const sendSuggestion = (text: string) => {
	input.value = text;
	send();
};

const clear = () => {
	confirmClearOpen.value = true;
};

const doClear = async () => {
	confirmClearOpen.value = false;
	try {
		await clearChatHistory();
		messages.value = [];
		conversationId.value = null;
	} catch {
		notification.error(t("chat.errorGeneric"));
	}
};

// Intercepta cliques em links internos (rotas do app) para navegar via router.
const onBubbleClick = (e: MouseEvent) => {
	const anchor = (e.target as HTMLElement)?.closest?.(
		"a.chat-link-btn",
	) as HTMLAnchorElement | null;
	if (!anchor) return;
	const href = anchor.getAttribute("href") || "";
	// só navega internamente com caminho de UMA barra (bloqueia "//" e "/\").
	if (isInternalHref(href) && href.startsWith("/")) {
		e.preventDefault();
		// No desktop a meia tela deixa a tela visível ao lado; em tela estreita
		// ela ocupa a largura toda e esconderia a página para onde o link leva.
		if (window.innerWidth < NARROW_SCREEN_MAX) panelSize.value = "compact";
		router.push(href);
	}
};

const stateFromStatus = (status?: PendingAction["status"]): ActionStatus =>
	status === "CONFIRMED"
		? "done"
		: status === "CANCELLED"
			? "cancelled"
			: status === "EXECUTING"
				? "executing"
				: "idle";

const showActionError = (action: PendingAction, err: any) => {
	const status = err?.response?.status;
	if (status === 403) {
		actionError.value[action.id] = t("chat.actions.noPermission");
		return;
	}
	const reason = widgetShouldToast(status) ? errorReason(err) : "";
	actionError.value[action.id] = reason
		? t("chat.actions.createErrorWithReason", { reason })
		: t("chat.actions.createError");
};

// Confirma uma proposta de criação → chama o service REAL do módulo.
// Reserva o card no servidor ANTES de criar: só uma aba (ou um clique) passa, e
// o cadastro não sai duplicado se a página recarregar no meio.
const confirmAction = async (action: PendingAction) => {
	applyDraft(action);
	actionError.value[action.id] = "";
	const missing = editableKeys(action).some(
		(key) => REQUIRED_EDIT_KEYS.has(key) && !String(action.payload[key] ?? "").trim(),
	);
	if (missing) {
		actionError.value[action.id] = t("validation.required");
		return;
	}
	actionState.value[action.id] = "creating";
	// Alterado no card: vai junto da reserva, para a recarga e a outra aba verem o valor criado.
	const edits = editedActions.value[action.id]
		? Object.fromEntries(editableKeys(action).map((key) => [key, action.payload[key]]))
		: undefined;

	try {
		await (edits
			? updateAssistantActionStatus(action.id, "EXECUTING", edits)
			: updateAssistantActionStatus(action.id, "EXECUTING"));
	} catch (err: any) {
		if (err?.response?.status === 409 && err.response.data?.status) {
			actionState.value[action.id] = stateFromStatus(err.response.data.status);
			return;
		}
		actionState.value[action.id] = "idle";
		showActionError(action, err);
		return;
	}

	try {
		await executePendingAction(action.resource, action.payload);
	} catch (err: any) {
		const status = err?.response?.status;
		// Só uma recusa do servidor (4xx, fora timeout) prova que nada foi gravado.
		// Rede caída, timeout ou 5xx podem ter criado e perdido a resposta: liberar
		// a reserva aí deixaria o próximo clique criar de novo.
		const rejected = !!status && status >= 400 && status < 500 && status !== 408;
		if (!rejected) {
			actionState.value[action.id] = "executing";
			return;
		}
		actionState.value[action.id] = "idle";
		showActionError(action, err);
		// Se nem a liberação gravar, a recarga mostra "confira na tela" em vez de outro Confirmar.
		updateAssistantActionStatus(action.id, "PENDING").catch(() => {});
		return;
	}

	actionState.value[action.id] = "done";
	notification.success(t("chat.actions.created"));
	// A tela da lista pode estar aberta ao lado (assistente em meia tela): o card
	// cria pelo service, sem passar pelo store, então avisa quem estiver exibindo.
	notifyEntityChanged(action.resource);
	// Era a dependência que faltava (ex.: a linha): segue sozinho com o cadastro pedido,
	// esperando o chat terminar a resposta em curso — send() recusa com ele ocupado.
	if (action.autoContinue) continueAfterDependency();
	// Sem gravar CONFIRMED, o card fica reservado: a recarga pede para conferir, não recria.
	updateAssistantActionStatus(action.id, "CONFIRMED").catch(() => {});
};

const continueAfterDependency = async () => {
	if (loading.value) await untilIdle();
	await send(t("chat.actions.continueMsg"));
};

const cancelAction = async (action: PendingAction) => {
	actionState.value[action.id] = "cancelled";
	try {
		await updateAssistantActionStatus(action.id, "CANCELLED");
	} catch (err: any) {
		if (err?.response?.status === 409) {
			actionState.value[action.id] = stateFromStatus(err.response.data?.status);
		}
	}
};

// O estado do assistente é singleton e sobrevive ao widget: sem isso, sair do
// layout (logout, sessão expirada) e voltar reabria o painel no tamanho anterior.
onUnmounted(close);

// Mensagem inicial disparada de fora (ex.: card de setup do dashboard).
// Trocou de tela (inclusive pelo link do próprio chat): a meia tela vai para o
// canto, para não cobrir os botões da tela nova. Só o caminho conta — filtro na
// query da mesma tela não mexe no painel.
watch(
	() => router.currentRoute.value.path,
	(to, from) => {
		if (to !== from) onNavigate();
	},
);

watch(seedPrompt, async (text) => {
	if (!text) return;
	seedPrompt.value = null;
	isOpen.value = true;
	if (!loadedHistory.value) await loadHistory();
	// Chat ocupado respondendo: `send()` recusaria e o texto ficaria parado no campo.
	if (loading.value) await untilIdle();
	// Vem de fora (card do dashboard, continuação do card): não é resposta
	// digitada, e não pode ser reinterpretada como resposta à pergunta da tela.
	await send(text);
});

// Pergunta feita pela tela (botão do dashboard): entra como fala do assistente,
// sem ida ao modelo. Depois do histórico — carregar o histórico troca a lista de
// mensagens e apagaria a pergunta.
watch(localQuestion, async (question) => {
	if (!question) return;
	localQuestion.value = null;
	isOpen.value = true;
	if (!loadedHistory.value) await loadHistory();
	if (loading.value) await untilIdle();
	// Pergunta da tela ainda sem resposta no fim da conversa: a nova a substitui,
	// em vez de empilhar a mesma pergunta duas vezes.
	const last = messages.value[messages.value.length - 1];
	if (last?.id.startsWith("local-question-")) messages.value.pop();
	messages.value.push({
		id: `local-question-${Date.now()}`,
		role: "ASSISTANT",
		content: question.text,
		createdAt: new Date().toISOString(),
		quickReplies: question.options.map((option) => option.label),
		replyMessages: Object.fromEntries(
			question.options.map((option) => [option.label, option.message]),
		),
		typedAnswerPrefix: question.typedAnswerPrefix,
	});
	await scrollToBottom();
	await focusInput();
});

const untilIdle = () =>
	new Promise<void>((resolve) => {
		const stop = watch(loading, (busy) => {
			if (busy) return;
			stop();
			resolve();
		});
	});
</script>

<style scoped lang="scss">
.chat-fab {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 2000;
}

.chat-panel {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 2000;
	width: 380px;
	max-width: calc(100vw - 32px);
	height: 560px;
	max-height: calc(100vh - 48px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	transition: all 0.2s ease;
}

// Aberto pelo card de configuração: meia tela à direita, deixando o dashboard
// visível ao lado. Em tela estreita os 560px mínimos viram largura total.
.chat-panel--large {
	top: 0;
	right: 0;
	bottom: 0;
	width: max(50vw, 560px);
	max-width: 100vw;
	height: 100vh;
	max-height: 100vh;
	border-radius: 0 !important;
}

.chat-title {
	min-width: 0;

	span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.chat-body {
	flex: 1 1 auto;
	overflow-y: auto;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	background: rgb(var(--v-theme-surface));
}

.chat-empty {
	margin: auto;
	text-align: center;
	max-width: 320px;
	padding: 16px 0;
}

.chat-suggestions {
	align-items: center;
}

.chat-msg {
	display: flex;
	max-width: 85%;
}

.chat-msg--user {
	align-self: flex-end;
	justify-content: flex-end;
}

.chat-msg--bot {
	align-self: flex-start;
}

.chat-bot-content {
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-width: 100%;
}

/* Botões de resposta: embaixo da pergunta, quebrando linha no celular. O
   espaço até a caixa vem do `gap` da coluna da fala. */
.chat-quick-replies {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.chat-quick-replies .v-btn {
	text-transform: none;
	letter-spacing: normal;
}

.chat-action-card {
	border: 1px solid rgba(var(--v-theme-primary), 0.35);
	border-radius: 10px;
	padding: 10px 12px;
	background: rgb(var(--v-theme-surface));
	font-size: 0.85rem;
}

.chat-action-title {
	display: flex;
	align-items: center;
	gap: 6px;
	font-weight: 700;
	color: rgb(var(--v-theme-primary));
	margin-bottom: 6px;
}

.chat-action-row {
	display: flex;
	justify-content: space-between;
	gap: 12px;
	padding: 2px 0;
}

.chat-action-label {
	opacity: 0.7;
	white-space: nowrap;
}

.chat-action-value {
	font-weight: 600;
	text-align: right;
	word-break: break-word;
}

.chat-action-input {
	flex: 1 1 auto;
	max-width: 70%;
}

.chat-action-swatch {
	display: inline-block;
	width: 18px;
	height: 18px;
	border-radius: 4px;
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.chat-action-executing {
	margin-top: 8px;
	padding: 6px 8px;
	border-radius: 6px;
	background: rgba(var(--v-theme-warning), 0.12);
	font-size: 0.8rem;
}

.chat-action-error {
	margin-top: 8px;
	padding: 6px 8px;
	border-radius: 6px;
	color: rgb(var(--v-theme-error));
	background: rgba(var(--v-theme-error), 0.08);
	font-size: 0.8rem;
}

.chat-action-buttons {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 2px;
	margin-top: 8px;

	// Três botões (Cancelar, Alterar, Confirmar) na janela pequena de 380px:
	// sem isso o primeiro vazava para fora do card.
	:deep(.v-btn) {
		padding-inline: 8px;
		min-width: 0;
	}
}

.chat-action-done {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 8px;
	color: rgb(var(--v-theme-success));
	font-weight: 600;
}

.chat-bubble {
	padding: 8px 12px;
	border-radius: 12px;
	font-size: 0.9rem;
	line-height: 1.4;
	white-space: pre-wrap;
	word-break: break-word;
}

.chat-msg--user .chat-bubble {
	background: rgb(var(--v-theme-primary));
	color: rgb(var(--v-theme-on-primary));
	border-bottom-right-radius: 4px;
}

.chat-msg--bot .chat-bubble {
	background: rgba(var(--v-theme-primary), 0.08);
	color: rgb(var(--v-theme-on-surface));
	border-bottom-left-radius: 4px;
}

/* Formatação do markdown renderizado (respostas do bot) */
.chat-markdown {
	white-space: normal;
}

.chat-markdown :deep(> *:first-child) {
	margin-top: 0;
}

.chat-markdown :deep(> *:last-child) {
	margin-bottom: 0;
}

.chat-markdown :deep(p) {
	margin: 0 0 8px;
}

.chat-markdown :deep(h1),
.chat-markdown :deep(h2),
.chat-markdown :deep(h3),
.chat-markdown :deep(h4) {
	margin: 12px 0 6px;
	font-size: 0.95rem;
	font-weight: 700;
	line-height: 1.3;
}

.chat-markdown :deep(ul),
.chat-markdown :deep(ol) {
	margin: 4px 0 8px;
	padding-left: 20px;
}

.chat-markdown :deep(li) {
	margin: 2px 0;
}

.chat-markdown :deep(a) {
	color: rgb(var(--v-theme-primary));
	text-decoration: underline;
}

/* Links renderizados como botão de ação */
.chat-markdown :deep(a.chat-link-btn) {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	margin: 3px 4px 3px 0;
	padding: 5px 14px;
	border-radius: 999px;
	background: rgb(var(--v-theme-primary));
	color: rgb(var(--v-theme-on-primary));
	font-size: 0.82rem;
	font-weight: 600;
	line-height: 1.2;
	text-decoration: none;
	cursor: pointer;
	transition: opacity 0.15s ease;
}

.chat-markdown :deep(a.chat-link-btn:hover) {
	opacity: 0.9;
}

.chat-markdown :deep(code) {
	background: rgba(var(--v-theme-on-surface), 0.08);
	padding: 1px 5px;
	border-radius: 4px;
	font-size: 0.85em;
}

.chat-markdown :deep(pre) {
	background: rgba(var(--v-theme-on-surface), 0.08);
	padding: 8px 10px;
	border-radius: 8px;
	overflow-x: auto;
	margin: 6px 0;
}

.chat-markdown :deep(pre code) {
	background: none;
	padding: 0;
}

.chat-markdown :deep(blockquote) {
	margin: 6px 0;
	padding-left: 10px;
	border-left: 3px solid rgba(var(--v-theme-primary), 0.4);
	color: rgb(var(--v-theme-on-surface));
	opacity: 0.85;
}

.chat-markdown :deep(table) {
	border-collapse: collapse;
	margin: 6px 0;
	font-size: 0.85em;
}

.chat-markdown :deep(th),
.chat-markdown :deep(td) {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.15);
	padding: 3px 8px;
}

.chat-footer {
	flex: 0 0 auto;
	padding: 8px 12px 6px;
	border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	background: rgb(var(--v-theme-surface));
}

.chat-disclaimer {
	margin: 4px 2px 0;
	text-align: center;
	line-height: 1.2;
}
</style>
